// ═══════════════════════════════════════════════════════════
// V2 API Client
// Core fetch wrapper with auth, token refresh, retry, ETag
// ═══════════════════════════════════════════════════════════

import type { PaginatedResponse, QueryOptions, LoginResponse, AuthTokens } from './types';

// In dev, Vite proxy handles CORS. In production, use Pages Function proxy.
const isDev = import.meta.env.DEV;
const API_BASE = isDev ? import.meta.env.VITE_V2_API_BASE : '/api/v2proxy';
const APP_ID = import.meta.env.VITE_V2_APP_ID;

// ── Token Cache ─────────────────────────────────────────

let tokens: AuthTokens | null = null;
let refreshPromise: Promise<void> | null = null;

export function setTokens(t: AuthTokens) { tokens = t; }
export function getTokens() { return tokens; }
export function clearTokens() { tokens = null; }

function isExpired(): boolean {
  if (!tokens) return true;
  return Date.now() > tokens.expiresAt - 60_000; // 60s buffer
}

// ── Auth ────────────────────────────────────────────────

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE}/v2/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error(`Login failed: ${res.status}`);
  const raw = await res.json();
  // Normalize API response — V2 returns { name, role } instead of { fullName, isAdmin }
  const u = raw.user || {};
  const data: LoginResponse = {
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    expiresIn: raw.expiresIn,
    user: {
      id: u.id ?? '',
      email: u.email ?? email,
      fullName: u.fullName || u.name || '',
      firstName: u.firstName || (u.name ?? '').split(' ')[0] || '',
      lastName: u.lastName || (u.name ?? '').split(' ').slice(1).join(' ') || '',
      role: u.role ?? '',
      isAdmin: u.isAdmin ?? (u.role === 'super_admin' || u.role === 'admin'),
    },
  };
  setTokens({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresAt: Date.now() + data.expiresIn * 1000,
  });
  return data;
}

async function refreshToken(): Promise<void> {
  if (!tokens?.refreshToken) throw new Error('No refresh token');
  const res = await fetch(`${API_BASE}/v2/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: tokens.refreshToken }),
  });
  if (!res.ok) {
    clearTokens();
    throw new Error('Token refresh failed');
  }
  const data = await res.json();
  setTokens({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresAt: Date.now() + data.expiresIn * 1000,
  });
}

async function ensureAuth(): Promise<string> {
  if (!tokens) throw new Error('Not authenticated');
  if (isExpired()) {
    if (!refreshPromise) {
      refreshPromise = refreshToken().finally(() => { refreshPromise = null; });
    }
    await refreshPromise;
  }
  return tokens!.accessToken;
}

// ── Core Fetch ──────────────────────────────────────────

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const accessToken = await ensureAuth();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
  });

  // Retry once on 401 for idempotent requests only — never retry POST/PATCH/DELETE
  // because retrying a POST would create duplicate records.
  const method = (options.method || 'GET').toUpperCase();
  const isIdempotent = method === 'GET' || method === 'HEAD';
  if (res.status === 401 && isIdempotent) {
    await refreshToken();
    const retryToken = tokens!.accessToken;
    const retry = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${retryToken}`,
        ...options.headers,
      },
    });
    if (!retry.ok) throw new Error(`API ${retry.status}: ${path}`);
    return retry.status === 204 ? (undefined as T) : retry.json();
  }
  if (res.status === 401 && !isIdempotent) {
    throw new Error(`API 401: ${path} — session expired, please refresh`);
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    console.error(`[API ${res.status}] ${path}`, body);
    throw new Error(`API ${res.status}: ${path} — ${body}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

// ── Table path helper ───────────────────────────────────

function tablePath(tableKey: string) {
  return `/v2/apps/${APP_ID}/tables/${tableKey}/records`;
}

// ── CRUD Operations ─────────────────────────────────────

// Normalize the V2 list response — the API returns { items, total } but our
// type (and all callers) expect { records, total }. Both field names are handled here.
function normalizeListResponse<T>(raw: unknown): PaginatedResponse<T> {
  const r = raw as Record<string, unknown>;
  const records = (r.records ?? r.items ?? r.data ?? []) as T[];
  return { records, total: (r.total as number) ?? records.length };
}

export async function fetchRecords<T = Record<string, unknown>>(
  tableKey: string,
  opts?: { limit?: number; offset?: number; sort?: { field: string; direction: 'asc' | 'desc' }; anonymous?: boolean },
): Promise<PaginatedResponse<T>> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.offset) params.set('offset', String(opts.offset));
  if (opts?.sort) {
    params.set('sort', opts.sort.field);
    params.set('order', opts.sort.direction);
  }
  const qs = params.toString();
  const path = `${tablePath(tableKey)}${qs ? `?${qs}` : ''}`;

  // anonymous=true: skip client-side auth check — Pages Function injects admin token for public pages
  if (opts?.anonymous) {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
    return normalizeListResponse<T>(await res.json());
  }

  const raw = await apiFetch<unknown>(path);
  return normalizeListResponse<T>(raw);
}

// V2 does not have a /query POST endpoint — fetch all and filter client-side.
export async function queryRecords<T = Record<string, unknown>>(
  tableKey: string,
  query: QueryOptions,
  opts?: { anonymous?: boolean },
): Promise<PaginatedResponse<T>> {
  // Fetch a large page to cover all records; apply sort param if supported
  const params = new URLSearchParams();
  params.set('limit', String(query.limit ?? 500));
  if (query.sort) {
    params.set('sort', query.sort.field);
    params.set('order', query.sort.direction);
  }
  const path = `${tablePath(tableKey)}?${params}`;

  let raw: unknown;
  if (opts?.anonymous) {
    // Skip client-side auth — Pages Function injects admin token for public pages
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
    raw = await res.json();
  } else {
    raw = await apiFetch<unknown>(path);
  }
  const result = normalizeListResponse<T>(raw);

  // Apply filters client-side
  if (query.filters && query.filters.length > 0) {
    result.records = result.records.filter(row => {
      return query.filters!.every(f => {
        const val = (row as Record<string, unknown>)[f.field];
        const fv = f.value;
        switch (f.operator) {
          case 'eq': return String(val ?? '') === String(fv ?? '');
          case 'neq': return String(val ?? '') !== String(fv ?? '');
          case 'gt': return Number(val) > Number(fv);
          case 'gte': return Number(val) >= Number(fv);
          case 'lt': return Number(val) < Number(fv);
          case 'lte': return Number(val) <= Number(fv);
          case 'contains': return String(val ?? '').toLowerCase().includes(String(fv ?? '').toLowerCase());
          case 'starts_with': return String(val ?? '').toLowerCase().startsWith(String(fv ?? '').toLowerCase());
          case 'ends_with': return String(val ?? '').toLowerCase().endsWith(String(fv ?? '').toLowerCase());
          case 'is_empty': return !val || String(val) === '';
          case 'is_not_empty': return !!val && String(val) !== '';
          case 'in': return Array.isArray(fv) && fv.map(String).includes(String(val ?? ''));
          case 'nin': return Array.isArray(fv) && !fv.map(String).includes(String(val ?? ''));
          default: return true;
        }
      });
    });
    result.total = result.records.length;
  }

  return result;
}

export async function createRecord<T = Record<string, unknown>>(
  tableKey: string,
  data: Record<string, unknown>,
  opts?: { anonymous?: boolean },
): Promise<T> {
  const path = tablePath(tableKey);
  if (opts?.anonymous) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
    return res.json();
  }
  return apiFetch<T>(path, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecord<T = Record<string, unknown>>(
  tableKey: string,
  recordId: number,
  data: Record<string, unknown>,
  opts?: { anonymous?: boolean },
): Promise<T> {
  const path = `${tablePath(tableKey)}/${recordId}`;
  if (opts?.anonymous) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
    return res.status === 204 ? (undefined as T) : res.json();
  }
  return apiFetch<T>(path, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteRecord(
  tableKey: string,
  recordId: number,
): Promise<void> {
  await apiFetch<void>(`${tablePath(tableKey)}/${recordId}`, { method: 'DELETE' });
}

export async function bulkCreate<T = Record<string, unknown>>(
  tableKey: string,
  records: Record<string, unknown>[],
): Promise<T[]> {
  return apiFetch<T[]>(`${tablePath(tableKey)}/bulk`, {
    method: 'POST',
    body: JSON.stringify({ records }),
  });
}

export async function bulkDelete(
  tableKey: string,
  ids: number[],
): Promise<void> {
  await apiFetch<void>(`${tablePath(tableKey)}/bulk`, {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
}
